import React from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import BottomPageNav from "@/components/nav/BottomPageNav";
import FloatingBackToTop from "@/components/nav/FloatingBackToTop";
import LanguageDropdown from "@/components/nav/LanguageDropdown";
import AdminTopBar from "@/components/layout/AdminTopBar";
import useRLContext from "@/hooks/useRLContext";
import { BarChart3, Users, FileText, Megaphone, ShieldCheck, ClipboardList, LogOut, Globe, Settings, UploadCloud, Inbox, FlaskConical, LineChart, ClipboardCheck, Share2, ShieldAlert, Wallet, Store, Tag, CalendarDays, Home, Eye, TrendingUp, Wrench, UserPlus } from "lucide-react";

const adminNav = [
  { to: "/admin", icon: BarChart3, key: "stats", end: true, roles: ["admin", "staff", "qa_manager", "institutional_observer"], category: "questionnaires", sub: "console" },
  { to: "/admin/stats-advanced", icon: LineChart, key: "statsAdvanced", roles: ["admin", "staff", "qa_manager", "institutional_observer"], category: "questionnaires", sub: "console" },
  { to: "/admin/users", icon: Users, key: "users", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  { to: "/admin/questionnaires", icon: FileText, key: "questionnaires", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  { to: "/admin/responses", icon: Inbox, key: "responses", roles: ["admin", "staff", "qa_manager"], category: "questionnaires", sub: "console" },
  { to: "/admin/import", icon: UploadCloud, key: "import", roles: ["admin"], category: "questionnaires", sub: "console" },
  { to: "/admin/interviewer", icon: ClipboardList, key: "interviewer", roles: ["admin", "staff", "qa_manager"], category: "questionnaires", sub: "console" },
  { to: "/admin/campaigns", icon: Megaphone, key: "campaigns", roles: ["admin", "staff", "qa_manager"], category: "questionnaires", sub: "console" },
  { to: "/admin/consent", icon: ShieldCheck, key: "consent", roles: ["admin", "staff", "qa_manager"], category: "questionnaires", sub: "console" },
  // A19-P1.3 — Referrals + Fraud queue (admin/staff only)
  { to: "/admin/referrals", icon: Share2, key: "referrals", i18n: "referrals.nav.adminReferrals", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  { to: "/admin/referral-campaigns", icon: Megaphone, key: "referralCampaigns", i18n: "referrals.adminCampaigns.navLabel", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  // A19-P6.4 — Internal Ad-slots admin (admin/staff only)
  { to: "/admin/ad-slots", icon: Megaphone, key: "adSlots", i18n: "referrals.adminAdSlots.navLabel", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  // A20-P10 — Marketplace review (admin/staff only)
  { to: "/admin/marketplace-requests", icon: Inbox, key: "marketplaceReview", i18n: "adminMarketplace.navLabel", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  // A20-P11 — Vendors + Offers (admin/staff only)
  { to: "/admin/vendors", icon: Store, key: "vendors", i18n: "adminVendors.navLabel", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  { to: "/admin/offers", icon: Tag, key: "offers", i18n: "adminOffers.navLabel", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  { to: "/admin/fraud", icon: ShieldAlert, key: "fraud", i18n: "referrals.nav.adminFraud", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  // A19-P4 — QA commission dashboard (qa_tester + qa_manager + admin/staff)
  { to: "/admin/qa-commissions", icon: Wallet, key: "qaCommissions", i18n: "referrals.nav.qaCommissions", roles: ["admin", "staff", "qa_manager", "qa_tester"], category: "questionnaires", sub: "console" },
  // D2 — Demo Preview Activity Log (admin/staff only).
  { to: "/admin/demo-activity-log", icon: Eye, key: "demoActivityLog", i18n: "admin.demoActivityLog.navLabel", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  // Marketplace Phase 1 — Demand Insights (admin/staff only).
  { to: "/admin/marketplace/demand", icon: TrendingUp, key: "marketplaceDemand", i18n: "marketplace.demand.navLabel", roles: ["admin", "staff"], category: "questionnaires", sub: "console" },
  { to: "/admin/settings", icon: Settings, key: "settings", roles: ["admin"], category: "questionnaires", sub: "console" },
  // QA / TEST area for questionnaires.
  { to: "/admin/testers", icon: FlaskConical, key: "testers", roles: ["admin", "staff", "qa_manager"], category: "questionnaires", sub: "test" },
  // Racket Lab Admin Shell — Sub-sprint A + SS-8/CP-1.3 RBAC sidebar.
  { to: "/admin/racket-lab", icon: Wrench, key: "racketLab", roles: ["admin", "staff", "qa_manager", "qa_tester"], category: "racketLab", sub: "console" },
  // CP-1.2 — Controlled Production cohort (admin-only provisioning).
  { to: "/admin/controlled-production", icon: UserPlus, key: "cpCohort", roles: ["admin"], category: "racketLab", sub: "test" },
  // GEAR-2 (Jun 2026) — internal My Gear preview · admin/staff only.
  { to: "/admin/gear/preview", icon: Wrench, key: "gearPreview", i18n: "admin.gearPreview", roles: ["admin", "staff"], category: "racketLab", sub: "test" },
  // WISHLIST-STORE-1 (Jun 2026) — Store access settings · admin/staff only.
  { to: "/admin/gear/store-access", icon: Wrench, key: "gearStoreAccess", i18n: "admin.gearStoreAccess", roles: ["admin", "staff"], category: "racketLab", sub: "test" },
  // WISHLIST-STORE-3 (Jun 2026) — Offer opportunity mock inbox · admin/staff only.
  { to: "/admin/gear/offer-opportunities", icon: Inbox, key: "gearOfferOpportunities", i18n: "admin.gearOfferOpportunities", roles: ["admin", "staff"], category: "racketLab", sub: "test" },
  // RACKET-LAB-FRONT-1 (Jun 2026) — Controlled QA profiles directory · admin/staff/qa_manager.
  { to: "/admin/racket-lab/controlled-profiles", icon: ClipboardList, key: "controlledProfiles", i18n: "admin.controlledProfiles", roles: ["admin", "staff", "qa_manager"], category: "racketLab", sub: "test" },
];

// Routes that QA managers cannot access. They're hard-redirected to /admin.
const QA_MANAGER_DENIED_PREFIXES = ["/admin/users", "/admin/controlled-production", "/admin/questionnaires", "/admin/import", "/admin/settings", "/admin/referrals", "/admin/referral-campaigns", "/admin/ad-slots", "/admin/marketplace-requests", "/admin/marketplace", "/admin/vendors", "/admin/offers", "/admin/fraud", "/admin/gear"];

// Read-only Institutional Observer can only see the two aggregated stats
// dashboards. Anything else hard-redirects to /admin so the role can
// never reach a route that exposes PII or per-row data.
const OBSERVER_ALLOWED_PREFIXES = ["/admin", "/admin/stats-advanced"];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const role = user?.role;
  const visibleNav = adminNav.filter((it) => !it.roles || it.roles.includes(role));

  // SS-8/CP-1.3 — RL-scoped profiles (shop_manager / operator /
  // racket_technician via membership, and qa_tester via JWT) live ONLY
  // inside the Racket Lab console: single nav entry + hard redirect.
  const { ctx: rlCtx, loading: rlLoading } = useRLContext();
  const RL_ONLY_PROFILES = ["shop_manager", "operator", "racket_technician"];
  const rlOnly =
    role === "qa_tester" ||
    (role === "staff" && RL_ONLY_PROFILES.includes(rlCtx?.profile));
  const navForRole = rlOnly
    ? visibleNav.filter((it) => it.key === "racketLab")
    : visibleNav;

  // A20-P10.1 — Marketplace review pending count for the sidebar badge.
  // Polls every 60s. Admin/staff only. Silently hides on any error.
  const [pendingMktCount, setPendingMktCount] = React.useState(0);
  React.useEffect(() => {
    if (role !== "admin" && role !== "staff") return undefined;
    let alive = true;
    const fetchCount = async () => {
      try {
        // A20-P13 — `offer_selected` joins the pending counter: it
        // signals "needs admin follow-up" so admin sees both review-
        // queue and follow-up tasks in a single badge.
        const r = await api.get(
          "/admin/marketplace-requests?status=ready_for_review,offer_selected&limit=1"
        );
        if (alive) setPendingMktCount(Number(r.data?.total || 0));
      } catch {
        if (alive) setPendingMktCount(0);
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [role]);

  // Hard-block forbidden routes for qa_manager (defense-in-depth alongside backend 403).
  if (role === "qa_manager" && QA_MANAGER_DENIED_PREFIXES.some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/admin" replace />;
  }
  // SS-8/CP-1.3 — wait for me-context before painting nav for staff/qa
  // (avoids flashing the full TEAM admin nav to RL-scoped users).
  if ((role === "staff" || role === "qa_tester") && rlLoading) {
    return (
      <div
        data-testid="admin-rl-context-loading"
        className="min-h-screen flex items-center justify-center text-sm text-slate-400"
      >
        …
      </div>
    );
  }
  if (rlOnly && !location.pathname.startsWith("/admin/racket-lab")) {
    return <Navigate to="/admin/racket-lab" replace />;
  }
  // Hard-block any non-stats route for institutional_observer.
  if (role === "institutional_observer") {
    const path = location.pathname;
    const allowed = path === "/admin" || path === "/admin/stats-advanced";
    if (!allowed) return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50/60 flex">
      <aside className="hidden md:flex flex-col w-64 border-r border-slate-200 bg-white p-4 sticky top-0 h-screen overflow-y-auto" data-testid="admin-sidebar">
        <Logo />
        <div className="mt-8 space-y-4">
          {(() => {
            // ADMIN-SIDEBAR-IA-1 — group nav items into two top-level
            // categories (questionnaires / racketLab) and two sub-buckets
            // each (console = PRODUCCIÓN, test = QA / TEST). Renders
            // visual headers only when the user has at least one item
            // inside that bucket, so RBAC trimming stays intact.
            const categoryOrder = ["questionnaires", "racketLab", "other"];
            const subOrder = ["console", "test"];
            const categoryKey = {
              questionnaires: "admin.sidebar_cat_questionnaires",
              racketLab: "admin.sidebar_cat_racket_lab",
              other: "admin.sidebar_cat_other",
            };
            const subKey = {
              console: "admin.sidebar_sub_console",
              test: "admin.sidebar_sub_test",
            };
            const grouped = {};
            navForRole.forEach((it) => {
              const cat = it.category || "other";
              const sub = it.sub || "console";
              grouped[cat] = grouped[cat] || {};
              grouped[cat][sub] = grouped[cat][sub] || [];
              grouped[cat][sub].push(it);
            });
            return categoryOrder
              .filter((cat) => grouped[cat])
              .map((cat) => (
                <div key={cat} data-testid={`admin-sidebar-cat-${cat}`} className="space-y-2">
                  <div
                    className="px-3 pt-2 text-base font-extrabold tracking-wider text-emerald-900 uppercase"
                    data-testid={`admin-sidebar-cat-${cat}-label`}
                  >
                    {t(categoryKey[cat])}
                  </div>
                  {subOrder
                    .filter((sub) => grouped[cat][sub])
                    .map((sub) => (
                      <div key={sub} className="space-y-1" data-testid={`admin-sidebar-sub-${cat}-${sub}`}>
                        <div
                          className="px-3 pt-1 text-xs font-bold tracking-widest text-emerald-700 uppercase"
                          data-testid={`admin-sidebar-sub-${cat}-${sub}-label`}
                        >
                          {t(subKey[sub])}
                        </div>
                        {grouped[cat][sub].map((it) => {
                          const showMktBadge =
                            it.key === "marketplaceReview" &&
                            (role === "admin" || role === "staff") &&
                            pendingMktCount > 0;
                          return (
                            <NavLink
                              key={it.key}
                              to={it.to}
                              end={it.end}
                              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${isActive ? "bg-emerald-50 text-emerald-800" : "text-slate-700 hover:bg-slate-50"}`}
                              data-testid={`admin-nav-${it.key}`}
                            >
                              <it.icon className="w-4 h-4" />
                              <span className="flex-1">{it.i18n ? t(it.i18n) : t(`admin.${it.key}`)}</span>
                              {showMktBadge && (
                                <span
                                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-bold tabular-nums leading-none"
                                  title={t("adminMarketplace.pendingTooltip").replace("{n}", pendingMktCount)}
                                  data-testid="admin-nav-marketplaceReview-badge"
                                >
                                  {pendingMktCount > 99 ? "99+" : pendingMktCount}
                                </span>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    ))}
                </div>
              ));
          })()}
        </div>
        <div className="mt-8 pt-4 border-t border-slate-100 space-y-2" data-testid="admin-sidebar-bottom">
          {/* Quick actions row: jump to public Home + Mi calendario from
              any admin page so we never lose access to the user-facing
              surfaces. */}
          <div className="flex items-center gap-1.5">
            <NavLink
              to="/"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-200 px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
              data-testid="admin-quick-home"
              title={t("common.home")}
            >
              <Home className="w-3.5 h-3.5" /> {t("common.home")}
            </NavLink>
            <NavLink
              to="/app/calendar"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800 hover:bg-emerald-100"
              data-testid="admin-quick-calendar"
              title={t("calendar.title")}
            >
              <CalendarDays className="w-3.5 h-3.5" /> {t("calendar.title")}
            </NavLink>
          </div>
          {/* QA-role-only shortcut so testers/managers can jump to the
              public panel and actually OPEN the assigned officials they
              need to evaluate. Admins / staff don't need this — they
              already have full questionnaire CRUD via the admin pane. */}
          {(role === "qa_manager" || role === "qa_tester") && (
            <NavLink
              to="/app"
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition"
              data-testid="qa-evaluate-link"
            >
              <ClipboardCheck className="w-4 h-4" />
              {t("nav.evaluateQuestionnaires")}
            </NavLink>
          )}
          <div className="rounded-lg border border-slate-200 p-3" data-testid="admin-user-card">
            <div className="text-xs text-slate-500">{user?.full_name}</div>
            <div className="text-xs font-medium text-emerald-700 capitalize">
              {role && role !== "user" ? t(`roles.${role}`) : role}
            </div>
            {role === "institutional_observer" && (
              <div
                className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5 text-[10px] leading-snug text-amber-900"
                data-testid="observer-readonly-banner"
              >
                {t("nav.observerReadOnly")}
              </div>
            )}
          </div>
          <div data-testid="admin-lang-switch">
            <LanguageDropdown variant="sidebar" testIdPrefix="admin-lang" />
          </div>
          <button onClick={() => { logout(); navigate("/"); }} className="w-full tsw-btn-ghost justify-start text-sm" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> {t("common.logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* POST-DEPLOY-FIX-04 (Sub-fase A) — global sticky top bar for
            admin. Sits inside <main> so it spans only the right column
            on desktop (the sidebar already occupies the left), and
            replaces the previous non-sticky mobile bar on small
            viewports. Always visible while scrolling. */}
        <AdminTopBar />
        <div className="md:hidden flex gap-1 p-2 overflow-x-auto tsw-scroll-hidden border-b border-slate-200 bg-white sticky top-14 z-20">
          {navForRole.map((it) => {
            const showMktBadge =
              it.key === "marketplaceReview" &&
              (role === "admin" || role === "staff") &&
              pendingMktCount > 0;
            return (
              <NavLink
                key={it.key}
                to={it.to}
                end={it.end}
                className={({ isActive }) => `whitespace-nowrap text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${isActive ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"}`}
              >
                {it.i18n ? t(it.i18n) : t(`admin.${it.key}`)}
                {showMktBadge && (
                  <span
                    className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[9px] font-bold tabular-nums leading-none"
                    data-testid="admin-nav-marketplaceReview-badge-mobile"
                  >
                    {pendingMktCount > 99 ? "99+" : pendingMktCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
          <BottomPageNav />
        </div>
      </main>
      <FloatingBackToTop />
    </div>
  );
};

export default AdminLayout;
